// Haversine formula to calculate distance between two coordinates in KM
export const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371; // Earth's radius in kilometers
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  const distance = R * c;
  return distance;
};

// Calculate delivery fee (Rs.50 per KM)
export const calculateDeliveryFee = (distance) => {
  const baseFee = 100; // Minimum delivery fee
  const perKmRate = 50;
  const calculatedFee = baseFee + (distance * perKmRate);
  return Math.min(Math.max(calculatedFee, baseFee), 2000); // Min Rs.100, Max Rs.2000
};

// Get current location using browser geolocation only
export const getCurrentLocation = () => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported by this browser.'));
      return;
    }

    const options = {
      enableHighAccuracy: true,
      timeout: 15000, // 15 seconds
      maximumAge: 60000 // 1 minute cache
    };

    const successCallback = (position) => {
      resolve({
        lat: position.coords.latitude,
        lng: position.coords.longitude,
        accuracy: position.coords.accuracy
      });
    };

    const errorCallback = (error) => {
      let errorMessage = 'Unable to get your GPS location. ';
      
      switch (error.code) {
        case error.PERMISSION_DENIED:
          errorMessage += 'Please allow location access in your browser settings to use automatic location detection.';
          break;
        case error.POSITION_UNAVAILABLE:
          errorMessage += 'GPS location information is unavailable. Please select your location manually on the map.';
          break;
        case error.TIMEOUT:
          errorMessage += 'GPS location request timed out. Please try again or select your location manually.';
          break;
        default:
          errorMessage += 'An unknown error occurred. Please select your location manually on the map.';
          break;
      }
      
      reject(new Error(errorMessage));
    };

    navigator.geolocation.getCurrentPosition(successCallback, errorCallback, options);
  });
};

// Sri Lanka districts coordinates for reference
export const sriLankaDistricts = {
  colombo: { lat: 6.9271, lng: 79.8612, name: 'Colombo' },
  gampaha: { lat: 7.0840, lng: 80.0098, name: 'Gampaha' },
  kalutara: { lat: 6.5894, lng: 79.9573, name: 'Kalutara' },
  kandy: { lat: 7.2906, lng: 80.6337, name: 'Kandy' },
  matale: { lat: 7.4675, lng: 80.6234, name: 'Matale' },
  nuwaraEliya: { lat: 6.9497, lng: 80.7891, name: 'Nuwara Eliya' },
  galle: { lat: 6.0535, lng: 80.2210, name: 'Galle' },
  matara: { lat: 5.9480, lng: 80.5353, name: 'Matara' },
  hambantota: { lat: 6.1249, lng: 81.1187, name: 'Hambantota' },
  jaffna: { lat: 9.6615, lng: 80.0255, name: 'Jaffna' },
  kilinochchi: { lat: 9.3803, lng: 80.3770, name: 'Kilinochchi' },
  mannar: { lat: 8.9773, lng: 79.9092, name: 'Mannar' },
  vavuniya: { lat: 8.7520, lng: 80.4870, name: 'Vavuniya' },
  mulativu: { lat: 9.2670, lng: 80.8142, name: 'Mullaitivu' },
  batticaloa: { lat: 7.7167, lng: 81.7000, name: 'Batticaloa' },
  ampara: { lat: 7.2975, lng: 81.6820, name: 'Ampara' },
  trincomalee: { lat: 8.5874, lng: 81.2152, name: 'Trincomalee' },
  kurunegala: { lat: 7.4863, lng: 80.3625, name: 'Kurunegala' },
  puttalam: { lat: 8.0376, lng: 79.8283, name: 'Puttalam' },
  anuradhapura: { lat: 8.3114, lng: 80.4037, name: 'Anuradhapura' },
  polonnaruwa: { lat: 7.9329, lng: 81.0081, name: 'Polonnaruwa' },
  badulla: { lat: 6.9934, lng: 81.0550, name: 'Badulla' },
  moneragala: { lat: 6.8726, lng: 81.3506, name: 'Moneragala' },
  ratnapura: { lat: 6.7057, lng: 80.3847, name: 'Ratnapura' },
  kegalle: { lat: 7.2539, lng: 80.3467, name: 'Kegalle' }
};

// Get district name from coordinates
export const getDistrictFromCoordinates = (lat, lng) => {
  let closestDistrict = null;
  let minDistance = Infinity;

  Object.values(sriLankaDistricts).forEach(district => {
    const distance = calculateDistance(lat, lng, district.lat, district.lng);
    if (distance < minDistance) {
      minDistance = distance;
      closestDistrict = district;
    }
  });

  return closestDistrict;
};